import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip
} from '@mui/material';
import { Close, AspectRatio, PhotoSizeSelectLarge } from '@mui/icons-material';

interface ImageResizeModalProps {
  open: boolean;
  onClose: () => void;
  imageElement: HTMLImageElement | null;
  onResize: (width: number, height: number, maintainAspect: boolean) => void;
}

const ImageResizeModal: React.FC<ImageResizeModalProps> = ({
  open,
  onClose,
  imageElement,
  onResize
}) => {
  const [width, setWidth] = useState(300);
  const [height, setHeight] = useState(200);
  const [originalWidth, setOriginalWidth] = useState(300);
  const [originalHeight, setOriginalHeight] = useState(200);
  const [maintainAspect, setMaintainAspect] = useState(true);
  const [aspectRatio, setAspectRatio] = useState(1.5);

  useEffect(() => {
    if (imageElement && open) {
      const currentWidth = imageElement.offsetWidth || imageElement.naturalWidth || 300;
      const currentHeight = imageElement.offsetHeight || imageElement.naturalHeight || 200;
      const naturalWidth = imageElement.naturalWidth || 300;
      const naturalHeight = imageElement.naturalHeight || 200;
      
      setWidth(currentWidth);
      setHeight(currentHeight);
      setOriginalWidth(naturalWidth);
      setOriginalHeight(naturalHeight);
      setAspectRatio(naturalWidth / naturalHeight);
    }
  }, [imageElement, open]);

  const handleWidthChange = (newWidth: number) => {
    setWidth(newWidth);
    if (maintainAspect) {
      setHeight(Math.round(newWidth / aspectRatio));
    }
  };

  const handleHeightChange = (newHeight: number) => {
    setHeight(newHeight);
    if (maintainAspect) {
      setWidth(Math.round(newHeight * aspectRatio));
    }
  };

  const handlePresetSize = (presetWidth: number, presetHeight?: number) => {
    setWidth(presetWidth);
    if (presetHeight) {
      setHeight(presetHeight);
    } else if (maintainAspect) {
      setHeight(Math.round(presetWidth / aspectRatio));
    }
  };

  const handleApply = () => {
    onResize(width, height, maintainAspect);
    onClose();
  };

  const handleReset = () => {
    setWidth(originalWidth);
    setHeight(originalHeight);
  };

  const presetSizes = [
    { label: 'Pequeno', width: 150 },
    { label: 'Médio', width: 300 },
    { label: 'Grande', width: 500 },
    { label: 'Extra Grande', width: 800 },
    { label: 'Original', width: originalWidth }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 10000 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhotoSizeSelectLarge />
          Redimensionar Imagem
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Preview da imagem */}
          {imageElement && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={imageElement.src}
                  alt={imageElement.alt}
                  style={{
                    width: Math.min(width, 400),
                    height: 'auto',
                    maxWidth: '100%',
                    display: 'block'
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Tamanhos predefinidos */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Tamanhos Predefinidos:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {presetSizes.map((preset) => (
                <Chip
                  key={preset.label}
                  label={`${preset.label} (${preset.width}px)`}
                  onClick={() => handlePresetSize(preset.width)}
                  variant={width === preset.width ? 'filled' : 'outlined'}
                  color={width === preset.width ? 'primary' : 'default'}
                  clickable
                />
              ))}
            </Box>
          </Box>

          {/* Controle de proporção */}
          <FormControlLabel
            control={
              <Checkbox
                checked={maintainAspect}
                onChange={(e) => setMaintainAspect(e.target.checked)}
                icon={<AspectRatio />}
                checkedIcon={<AspectRatio color="primary" />}
              />
            }
            label="Manter proporção original"
          />

          {/* Controles de dimensão */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Largura (px):
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={width}
                onChange={(e) => handleWidthChange(parseInt(e.target.value) || 0)}
                inputProps={{ min: 50, max: 1200 }}
                size="small"
              />
              <Slider
                value={width}
                onChange={(_, value) => handleWidthChange(value as number)}
                min={50}
                max={1200}
                step={10}
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Altura (px):
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={height}
                onChange={(e) => handleHeightChange(parseInt(e.target.value) || 0)}
                inputProps={{ min: 50, max: 1200 }}
                size="small"
                disabled={maintainAspect}
              />
              <Slider
                value={height}
                onChange={(_, value) => handleHeightChange(value as number)}
                min={50}
                max={1200}
                step={10}
                sx={{ mt: 1 }}
                disabled={maintainAspect}
              />
            </Grid>
          </Grid>

          {/* Informações da imagem */}
          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Informações da Imagem:
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Tamanho Original: {originalWidth} × {originalHeight}px
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Novo Tamanho: {width} × {height}px
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Proporção: {aspectRatio.toFixed(2)}:1
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Escala: {((width / originalWidth) * 100).toFixed(0)}%
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleReset} color="secondary">
          Restaurar Original
        </Button>
        <Button onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleApply} variant="contained">
          Aplicar Redimensionamento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageResizeModal; 